import type { components, PaigeClient } from '@paige/api-client';
import { tenantHeader, unwrap } from './http';
import type { Device, DeviceAuthorizationRequest } from './types';

/** What the connect page posts for the computer that opened it (code with PKCE, loopback redirect). */
export type DeviceConnectStart = components['schemas']['DeviceConnectStart'];
/** Where the page sends the browser: the computer's loopback address with the one-time code. */
export type DeviceConnectCode = components['schemas']['DeviceConnectCode'];

/**
 * Connected devices (docs/engineering/folder-monitor.md): the company's computers and copiers with
 * their watched folders, revoking one and removing a folder of a computer that is gone (admins), and
 * the Allow this device page's two calls, the request behind a code and the decision on it, and the
 * Connect this computer page's one, which connects Paige Scan into an admin's company.
 */
export interface DevicesApi {
  list(tenant: string): Promise<Device[]>;
  revoke(tenant: string, deviceId: string): Promise<void>;
  removeFolder(tenant: string, deviceId: string, folderId: string): Promise<void>;
  /** The open request behind the code a device shows; 404 when there is none. */
  lookUp(userCode: string): Promise<DeviceAuthorizationRequest>;
  /** Allows the device into the company (an admin's), or refuses it. */
  decide(tenant: string, userCode: string, allow: boolean): Promise<{ allowed: boolean }>;
  /** Connects the computer that opened the page into the company (an admin's); the answer says where to go. */
  connect(tenant: string, start: DeviceConnectStart): Promise<DeviceConnectCode>;
}

export function devicesApi(client: PaigeClient): DevicesApi {
  return {
    async list(tenant) {
      return unwrap(
        await client.GET('/company/devices', { params: { header: tenantHeader(tenant) } }),
      ) as Device[];
    },
    async revoke(tenant, deviceId) {
      unwrap(
        await client.DELETE('/company/devices/{deviceId}', {
          params: { header: tenantHeader(tenant), path: { deviceId } },
        }),
      );
    },
    async removeFolder(tenant, deviceId, folderId) {
      unwrap(
        await client.DELETE('/company/devices/{deviceId}/folders/{folderId}', {
          params: { header: tenantHeader(tenant), path: { deviceId, folderId } },
        }),
      );
    },
    ...connectingCalls(client),
  };
}

/** The pages a person connects a device on: the code's request and its decision, and a computer's connect. */
function connectingCalls(client: PaigeClient): Pick<DevicesApi, 'lookUp' | 'decide' | 'connect'> {
  return {
    async lookUp(userCode) {
      return unwrap(
        await client.GET('/devices/authorizations/{userCode}', { params: { path: { userCode } } }),
      ) as DeviceAuthorizationRequest;
    },
    async decide(tenant, userCode, allow) {
      const params = { header: tenantHeader(tenant), path: { userCode } };
      const answer = allow
        ? await client.POST('/devices/authorizations/{userCode}/approve', { params })
        : await client.POST('/devices/authorizations/{userCode}/deny', { params });
      return unwrap(answer) as { allowed: boolean };
    },
    async connect(tenant, start) {
      return unwrap(
        await client.POST('/devices/connect', {
          params: { header: tenantHeader(tenant) },
          body: start,
        }),
      ) as DeviceConnectCode;
    },
  };
}
